import { Heart } from 'lucide-react';
import { usePostLikes, useToggleLike } from '@/hooks/useLikes';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { useMembershipContext } from '@/components/layout/AppLayout';

interface LikeButtonProps {
  postId: string;
  initialCount?: number;
}

export function LikeButton({ postId, initialCount = 0 }: LikeButtonProps) {
  const { data: likesData, isLoading } = usePostLikes(postId);
  const { toggleLike, isPending } = useToggleLike();
  const { toast } = useToast();
  const { isPending: isMembershipPending } = useMembershipContext();

  const count = likesData?.count ?? initialCount;
  const hasLiked = likesData?.hasLiked ?? false;

  const handleClick = async () => {
    if (isPending || isMembershipPending) return;
    try {
      await toggleLike(postId, hasLiked);
    } catch (error: any) {
      toast({
        title: 'Unable to update like',
        description: error?.message || 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={isPending || isLoading || isMembershipPending}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs transition-colors',
        'border disabled:opacity-50',
        hasLiked
          ? 'bg-primary/10 border-primary text-primary'
          : 'bg-muted/50 border-border text-muted-foreground hover:bg-muted hover:border-border/80'
      )}
    >
      <Heart
        className={cn('h-3.5 w-3.5', hasLiked && 'fill-current')}
      />
      <span>{count}</span>
    </button>
  );
}
